import { IAgentConfigDeprecatedService } from '@agent/core/agent/agent-config-deprecated.service';
import { IAgentStatusDeprecatedService } from '@agent/core/agent/agent-status-deprecated.service';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { AppExternalService } from '@commons/core/ui/app-external.service';
import { ChannelUtil } from '@commons/util/helper/channel';
import { optToPromise } from '@commons/util/utilities/option';
import { Store } from '@ngrx/store';
import { IDeferred, ILogService, IPromise, IQService } from 'angular';
import { filter, mergeMap, Subscription, withLatestFrom } from 'rxjs';

import {
  ActivityFacade,
  ActivityStatus,
  ActivityType,
  AgentChannelFacade,
  AgentStatus,
  AgentUserFacade,
  BookingStatus,
  ChannelType,
  COMETD_REQUEST_REJECTED,
  CSR_FACADE_PREFIX,
  fStatus,
  fTruthy,
  isAgentReady,
  MessagingService,
  None,
  Option,
  Transition,
  waitToLoad,
} from '@contact/upgrade';

import { CHAT_FRAME_NAME, ChatIadvizeFrame, IChatIadvizeFrame } from './models/chat-iadvize-frame';

const MODULE_NAME = 'iAdvizeService';

export interface IIAdvizeService {
  get(): IPromise<IChatIadvizeFrame>;

  open(): IPromise<IChatIadvizeFrame>;

  close(): IPromise<any>;
}

export class IAdvizeService implements IIAdvizeService {
  static $inject: Array<string> = [
    '$log',
    '$q',
    'httpDeprecated',
    'messaging',
    'externalApplications',
    'agentConfigDeprecatedService',
    'toastDeprecatedService',
    'contactDeprecatedService',
    'agentStatusDeprecatedService',
    'ngrxStoreService',
    'agentChannelFacade',
    'activityFacade',
    'agentUserFacade',
  ];

  public name = MODULE_NAME;

  private chatFrame: Option<IDeferred<IChatIadvizeFrame>> = None;
  private subscriptions: Subscription[] = [];
  private closeDeferred: IDeferred<any> = this.$q.defer();
  private isCurrentClose: boolean = false;

  constructor(
    protected readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly messaging: MessagingService,
    private readonly externalAppService: AppExternalService,
    private readonly agentConfigDeprecatedService: IAgentConfigDeprecatedService,
    private readonly toastDeprecatedService: ToastDeprecatedService,
    private readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly agentStatusDeprecatedService: IAgentStatusDeprecatedService,
    private readonly store$: Store,
    private readonly agentChannelFacade: AgentChannelFacade,
    private readonly activityFacade: ActivityFacade,
    private readonly agentUserFacade: AgentUserFacade
  ) {
    let task$ = this.activityFacade.getTransitionsByType(ActivityType.TASK);
    // retrieve PAUSE with status
    let pause$ = this.activityFacade.getTransitionsByType(ActivityType.PAUSE);

    task$
      .pipe(
        fStatus(ActivityStatus.ACCEPTED),
        withLatestFrom(
          this.agentChannelFacade.has(ChannelUtil.INCOMING_CHAT_IADVIZE),
          this.agentChannelFacade.isActive(ChannelUtil.INCOMING_CHAT_IADVIZE)
        ),
        filter(([_, hasChannel, active]) => hasChannel && active)
      )
      .subscribe(([status]) => {
        if (status) this.close();
      });

    pause$
      .pipe(
        fStatus(ActivityStatus.ACCEPTED),
        withLatestFrom(
          this.agentChannelFacade.has(ChannelUtil.INCOMING_CHAT_IADVIZE),
          this.agentChannelFacade.isActive(ChannelUtil.INCOMING_CHAT_IADVIZE)
        ),
        filter(([_, hasChannel, active]) => hasChannel && active)
      )
      .subscribe(([status]) => {
        if (status) this.close();
      });

    // pause state success
    this.activityFacade.success$
      .pipe(
        withLatestFrom(
          this.agentChannelFacade.has(ChannelUtil.INCOMING_CHAT_IADVIZE),
          this.agentChannelFacade.isActive(ChannelUtil.INCOMING_CHAT_IADVIZE)
        ),
        filter(([_, hasChannel, active]) => hasChannel && active)
      )
      .subscribe(([transition]) => this.pauseStateListener(transition));

    this.messaging
      .forRemote()
      .listen$<any>(COMETD_REQUEST_REJECTED)
      .pipe(filter(rejection => rejection.uuid === 'chat-basket'))
      .subscribe(() => {
        this.$log.error('[iadvize] Failed to inject chat request.');
        this.toastDeprecatedService.alert('agent:toast.chat_open_failed');
        this.close();
        this.agentChannelFacade.unactive([ChannelUtil.INCOMING_CHAT_IADVIZE]);
        this.chatFrame = None;
      });

    this.agentChannelFacade
      .isActive(ChannelUtil.INCOMING_CHAT_IADVIZE)
      .pipe(
        withLatestFrom(this.agentChannelFacade.has(ChannelUtil.INCOMING_CHAT_IADVIZE), this.agentUserFacade.transition$),
        filter(([_, hasChannel]) => hasChannel)
      )
      .subscribe(([isActive, _, statusAgent]) => {
        if (isActive && (statusAgent.current === AgentStatus.AVAILABLE || statusAgent.current === AgentStatus.WORKING)) {
          this.open();
        } else if (!isActive) {
          this.close();
        }
      });

    // Agent state event
    this.agentStatusDeprecatedService
      .success()
      .pipe(
        withLatestFrom(
          this.agentChannelFacade.has(ChannelUtil.INCOMING_CHAT_IADVIZE),
          this.agentChannelFacade.isActive(ChannelUtil.INCOMING_CHAT_IADVIZE)
        ),
        filter(([_, hasChannel, active]) => hasChannel && active)
      )
      .subscribe(([event]) => {
        if (event.newState === AgentStatus.AVAILABLE) {
          this.open();
        } else if (event.newState !== AgentStatus.WORKING) {
          this.close();
        }
      });

    this.store$
      .pipe(
        waitToLoad([isAgentReady]),
        mergeMap(() => this.agentChannelFacade.has(ChannelUtil.INCOMING_CHAT_IADVIZE)),
        fTruthy(),
        withLatestFrom(this.agentChannelFacade.isActive(ChannelUtil.INCOMING_CHAT_IADVIZE), this.agentUserFacade.status$)
      )
      .subscribe(([_, active, status]) => {
        if (status === AgentStatus.AVAILABLE || status === AgentStatus.WORKING) {
          if (active) {
            this.open().then(
              () => {},
              () => setTimeout(() => this.agentChannelFacade.unactive([ChannelUtil.INCOMING_CHAT_IADVIZE]), 1000)
            );
          } else {
            this.close();
          }
        } else {
          this.close();
        }
      });
  }

  private delay(interval: number) {
    let defer = this.$q.defer();
    setTimeout(() => defer.resolve(), interval);
    return defer.promise;
  }

  open(): IPromise<IChatIadvizeFrame> {
    if (!this.agentConfigDeprecatedService.hasChannel(ChannelType.CHAT_IADVIZE)) {
      this.toastDeprecatedService.alert('agent:toast.chat_open_error');
      this.chatFrame = None;
      return this.$q.reject();
    }

    let agentStatusDeprecatedService = this.agentStatusDeprecatedService.getCurrentAgentState();
    if (agentStatusDeprecatedService !== AgentStatus.AVAILABLE && agentStatusDeprecatedService !== AgentStatus.WORKING) {
      this.toastDeprecatedService.alert('agent:toast.chat_open_error');
      this.chatFrame = None;
      return this.$q.reject();
    }

    if (this.chatFrame.isEmpty()) {
      this.chatFrame = Option.of(this.$q.defer());
    } else {
      if (this.isCurrentClose) {
        // wait
        return this.delay(500).then(() => this.open());
      } else {
        return this.chatFrame.get().promise;
      }
    }

    if (this.agentConfigDeprecatedService.getChatIadvizeServiceUrl().isEmpty()) {
      this.$log.error('[iadvize] No chat service URL found.');
      this.toastDeprecatedService.alert('agent:toast.chat_open_error');
      this.chatFrame = None;
      return this.$q.reject();
    }

    this.$log.info(`[iadvize] Opening chat...`);

    this.subscriptions.push(
      this.contactDeprecatedService
        .contactCreated$()
        .pipe(filter(event => event.channel.type === ChannelType.CHAT_IADVIZE))
        .subscribe(() => this.openFrame())
    );

    this.subscriptions.push(
      this.contactDeprecatedService
        .contactState$()
        .pipe(
          filter(event => event.contact.channel.type === ChannelType.CHAT_IADVIZE),
          filter(event => event.state === BookingStatus.CLOSED)
        )
        .subscribe(() => this.closeFrame())
    );

    if (this.contactDeprecatedService.getContactsByChannelType(ChannelType.CHAT_IADVIZE).length > 0) {
      this.openFrame();
    } else {
      this.createChatIadvizeContat().catch(() => this.chatFrame.forEach(frame => frame.reject()));
    }
    return this.chatFrame.get().promise.catch(() => {
      this.toastDeprecatedService.alert('agent:toast.chat_open_error');
      this.chatFrame = None;
      return this.$q.reject();
    });
  }

  get(): IPromise<IChatIadvizeFrame> {
    return this.chatFrame.map(frame => frame.promise).getOrElseFunc(() => this.$q.reject());
  }

  close(): IPromise<any> {
    if (this.chatFrame.isEmpty()) {
      return this.$q.reject();
    }
    this.$log.info('[iadvize] Closing chat...');
    this.closeChatContact().catch(() => {
      this.$log.error('[iadvize] close chat failed !');
      this.closeDeferred.reject();
    });

    return this.closeDeferred.promise.catch(() => {
      this.toastDeprecatedService.alert('agent:toast.chat_close_error');
      return this.$q.reject();
    });
  }

  private pauseStateListener(transition: Transition<ActivityStatus>) {
    if (transition.current === ActivityStatus.ACCEPTED) {
      this.close();
    }
    if (transition.current === ActivityStatus.CANCELED) {
      this.open();
    }
  }

  private loadUrl(frame: HTMLIFrameElement, url: string): IPromise<HTMLIFrameElement> {
    let deferred = this.$q.defer<HTMLIFrameElement>();
    let listener = () => {
      frame.removeEventListener('load', listener);
      this.$log.debug('[iadvize] Frame loaded !');
      if (document.getElementsByClassName('formLogin').length > 0) {
        this.$log.error('[iadvize] Login failed.');
        deferred.reject();
      } else {
        deferred.resolve(frame);
      }
    };
    frame.addEventListener('load', listener);
    this.$log.info(`[iadvize] Loading URL[${url}]`);
    frame.src = url;
    return deferred.promise;
  }

  private createChatIadvizeContat(): IPromise<any> {
    this.$log.debug('[iadvize] Creating chat contact...');
    return this.httpDeprecated
      .asPromise()
      .get(CSR_FACADE_PREFIX + '/openChatBasket')
      .then(result => {
        if (result.data === 'false') {
          return this.$q.reject();
        }
        return result;
      });
  }

  private closeChatContact(): IPromise<any> {
    return this.httpDeprecated
      .asPromise()
      .get(CSR_FACADE_PREFIX + '/closeChatBasket')
      .then(result => {
        if (result.data === 'false') {
          return this.$q.reject();
        }
        return result;
      });
  }

  private openFrame(): void {
    optToPromise(this.externalAppService.createApplication(CHAT_FRAME_NAME), this.$q)
      .then(frame => {
        let url = this.agentConfigDeprecatedService.getChatIadvizeServiceUrl().getOrElse('');
        return this.loadUrl(frame, url);
      })
      .then(
        () => {
          this.$log.info(`[iadvize] Chat opened.`);
          this.closeDeferred = this.$q.defer();
          this.chatFrame.forEach(chatFrame => chatFrame.resolve(new ChatIadvizeFrame(this.externalAppService)));
        },
        () => {
          this.toastDeprecatedService.alert('agent:toast.chat_login_failed');
          this.chatFrame.forEach(chatFrame => chatFrame.reject());
        }
      );
  }

  private closeFrame(): void {
    this.isCurrentClose = true;
    this.get().then(frame => frame.hide());
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];

    let container = this.externalAppService.getApplicationContainer(CHAT_FRAME_NAME);
    optToPromise(container, this.$q)
      .then(frame => {
        // Detect navigator
        if (navigator.userAgent.indexOf('MSIE') !== -1 || !!navigator.userAgent.match(/Trident.*rv\:11\./)) {
          // CASE IE
          let url = frame.src.substr(0, frame.src.indexOf('/', 10));
          let logoutUrl = url + '/admin/logout';
          return this.loadUrl(frame, logoutUrl);
        } else {
          // CASE OTHER
          let url = new URL(frame.src);
          let logoutUrl = `${url.protocol}//${url.host}/admin/logout`;
          return this.loadUrl(frame, logoutUrl);
        }
      })
      .then(
        () => {
          this.$log.info('[iadvize] Service logged out.');
          this.closeDeferred.resolve();
        },
        () => this.closeDeferred.reject()
      )
      .finally(() => {
        this.chatFrame = None;
        this.isCurrentClose = false;
        this.externalAppService.removeApplication(CHAT_FRAME_NAME);
        this.$log.info('[iadvize] Chat closed.');
      });
  }
}

export default MODULE_NAME;
